import gql from "graphql-tag";

// Get shipping labels for an order
export const GET_ORDER_SHIPPING_LABELS = gql`
  query GetOrderShippingLabels($orderId: Int!) {
    shippingLabels(orderId: $orderId) {
      shippingLabelId
      labelId
      shipmentId
      trackingNumber
      carrierCode
      carrierFriendlyName
      serviceCode
      shippingCost
      labelData
      labelFormat
      isVoided
      isReturnLabel
      isTestLabel
      trackingStatus
      estimatedDeliveryDate
      createdAt
      updatedAt
    }
  }
`;

// Get available shipping carriers
export const GET_SHIPPING_CARRIERS = gql`
  query GetShippingCarriers {
    carriers {
      carrierId
      carrierCode
      friendlyName
      nickname
      accountNumber
      isPrimary
      requiresFunding
      balance
      hasMultiPackageSupport
      supportsLabelMessages
      lastSyncedAt
      services {
        carrierServiceId
        carrierCode
        serviceCode
        name
        isDomestic
        isInternational
        isMultiPackageSupported
      }
      packages {
        carrierPackageId
        carrierCode
        packageCode
        name
        description
        packageTypeId
        packageType {
          packageTypeId
          name
          description
          length
          width
          height
          weight
          weightLimit
          isActive
          isSystemDefined
          isCarrierPackage
          carrierCode
          carrierPackageCode
          qrCodeData
        }
      }
    }
  }
`;

// Get carrier packages
export const GET_CARRIER_PACKAGES = gql`
  query GetCarrierPackages($carrierCode: String) {
    carrierPackages(carrierCode: $carrierCode) {
      carrierPackageId
      carrierId
      carrierCode
      packageCode
      name
      description
      packageTypeId
      packageType {
        packageTypeId
        name
        description
        length
        width
        height
        weight
        weightLimit
        isActive
        isSystemDefined
        isCarrierPackage
        carrierCode
        carrierPackageCode
      }
    }
  }
`;

// Get package types
export const GET_PACKAGE_TYPES = gql`
  query GetPackageTypes($isCarrierPackage: Boolean, $carrierCode: String, $skip: Int, $take: Int) {
    packageTypes(
      isCarrierPackage: $isCarrierPackage
      carrierCode: $carrierCode
      skip: $skip
      take: $take
    ) {
      items {
        packageTypeId
        name
        description
        length
        width
        height
        weight
        weightLimit
        isSystemDefined
        isActive
        isCarrierPackage
        carrierCode
        carrierPackageCode
        qrCodeData
        volume
        createdAt
        modifiedAt
        createdBy
        modifiedBy
        usageLogs {
          packageUsageLogId
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      totalCount
    }
  }
`;

// Get shipping rates for an order
export const GET_SHIPPING_RATES = gql`
  query GetShippingRates($orderId: Int!, $pickTicketId: Long, $packageDetails: PackageDetailsInput!, $emails: String, $includeAllCarriers: Boolean, $labelMessages: LabelMessagesInput, $shipToOverride: ShipToOverrideInput) {
    shippingRates(orderId: $orderId, pickTicketId: $pickTicketId, packageDetails: $packageDetails, emails: $emails, includeAllCarriers: $includeAllCarriers, labelMessages: $labelMessages, shipToOverride: $shipToOverride) {
      shipmentId
      rates {
        rateId
        serviceCode
        serviceName
        carrierCode
        carrierFriendlyName
        shippingAmount {
          amount
          currency
        }
        insuranceAmount {
          amount
          currency
        }
        confirmationAmount {
          amount
          currency
        }
        deliveryDays
        estimatedDeliveryDate
        guaranteedService
        shipDate
        carrierDeliveryDays
        zone
        packageType
        includesTracking
        includesInsurance
      }
      errors {
        carrierCode
        message
      }
    }
  }
`;

// Get shipping rates for an order (multi-package support)
export const GET_SHIPPING_RATES_MULTI_PACKAGE = gql`
  query GetShippingRatesMultiPackage(
    $orderId: Int!
    $pickTicketId: Long
    $multiPackageDetails: MultiPackageDetailsInput!
    $emails: String
    $includeAllCarriers: Boolean
    $labelMessages: LabelMessagesInput
    $shipToOverride: ShipToOverrideInput
  ) {
    shippingRates(
      orderId: $orderId
      pickTicketId: $pickTicketId
      multiPackageDetails: $multiPackageDetails
      emails: $emails
      includeAllCarriers: $includeAllCarriers
      labelMessages: $labelMessages
      shipToOverride: $shipToOverride
    ) {
      shipmentId
      rates {
        rateId
        serviceCode
        serviceName
        carrierCode
        carrierFriendlyName
        shippingAmount {
          amount
          currency
        }
        insuranceAmount {
          amount
          currency
        }
        confirmationAmount {
          amount
          currency
        }
        deliveryDays
        estimatedDeliveryDate
        guaranteedService
        shipDate
        carrierDeliveryDays
        zone
        packageType
        includesTracking
        includesInsurance
      }
      errors {
        carrierCode
        message
      }
    }
  }
`;

// Create shipping label mutation
export const CREATE_SHIPPING_LABEL = gql`
  mutation CreateShippingLabel(
    $orderId: Int!
    $pickTicketId: Long
    $rateId: String!
    $shipmentId: String!
    $testMode: Boolean
    $labelFormat: String
    $labelLayout: String
    $printerId: String
    $emails: String
    $includeReturnLabel: Boolean
    $returnLabelMessages: LabelMessagesInput
    $returnLabelPackageSequences: [Int!]
    $returnLabelRmaPerPackage: [ReturnLabelPackageRmaInput!]
  ) {
    createShippingLabel(
      orderId: $orderId
      pickTicketId: $pickTicketId
      rateId: $rateId
      shipmentId: $shipmentId
      testMode: $testMode
      labelFormat: $labelFormat
      labelLayout: $labelLayout
      printerId: $printerId
      emails: $emails
      includeReturnLabel: $includeReturnLabel
      returnLabelMessages: $returnLabelMessages
      returnLabelPackageSequences: $returnLabelPackageSequences
      returnLabelRmaPerPackage: $returnLabelRmaPerPackage
    ) {
      label {
        shippingLabelId
        labelId
        trackingNumber
        labelData
        shippingCost
        packageSequence
        totalPackages
        combinedLabelData
      }
      shipment {
        shipmentId
        carrierCode
        serviceCode
      }
      returnLabel {
        shippingLabelId
        labelId
        trackingNumber
        labelData
        shippingCost
        isReturnLabel
      }
      returnLabels {
        shippingLabelId
        labelId
        trackingNumber
        labelData
        shippingCost
        isReturnLabel
        packageSequence
        totalPackages
      }
      allLabels {
        shippingLabelId
        labelId
        trackingNumber
        labelData
        shippingCost
        isReturnLabel
        packageSequence
        totalPackages
        combinedLabelData
      }
      packageCount
      isMultiPackage
      warnings
    }
  }
`;

// Void shipping label mutation
export const VOID_SHIPPING_LABEL = gql`
  mutation VoidShippingLabel($labelId: String!) {
    voidShippingLabel(labelId: $labelId) {
      success
      message
      returnLabelVoided
    }
  }
`;

// Create return label mutation
export const CREATE_RETURN_LABEL = gql`
  mutation CreateReturnLabel($labelId: String!, $returnLabelMessages: LabelMessagesInput) {
    createReturnLabel(labelId: $labelId, returnLabelMessages: $returnLabelMessages) {
      label {
        shippingLabelId
        labelId
        trackingNumber
        labelData
        isReturnLabel
      }
    }
  }
`;

// Create shipping manifest mutation
export const CREATE_SHIPPING_MANIFEST = gql`
  mutation CreateShippingManifest($labelIds: [String!]!) {
    createShippingManifest(labelIds: $labelIds) {
      manifestId
      manifestDownload
      manifestData
      carrierCode
      createdAt
    }
  }
`;

// Track package
export const TRACK_PACKAGE = gql`
  query TrackPackage($trackingNumber: String!, $carrierCode: String!) {
    trackPackage(trackingNumber: $trackingNumber, carrierCode: $carrierCode) {
      trackingNumber
      carrierCode
      status
      estimatedDeliveryDate
      actualDeliveryDate
      events {
        timestamp
        location
        description
        status
      }
    }
  }
`;

// Track shipping selection for learning
export const TRACK_SHIPPING_SELECTION = gql`
  mutation TrackShippingSelection($input: TrackShippingSelectionInput!) {
    trackShippingSelection(input: $input) {
      success
    }
  }
`;

// Get order with shipping method details
export const GET_ORDER_WITH_SHIPPING_METHOD = gql`
  query GetOrderWithShippingMethod($orderId: Int!) {
    salesOrderById(id: $orderId) {
      salesOrderId
      orderNumber
      status
      salesRepId
      createdBy
      quote {
        quoteId
        salesRepId
        quoteCustomer {
          contactId
          contact {
            contactId
            firstName
            lastName
            email
            workPhoneNumber
            cellPhoneNumber
            primaryContact
            isActive
          }
        }
      }
      customer {
        contactId
        shipTo
        customer {
          organization
          customerNumber
          addresses {
            addressId
            addressLine1
            addressLine2
            city
            state
            postalCode
            isPrimaryAddress
          }
          contacts {
            contactId
            primaryContact
            firstName
            lastName
            email
            isActive
          }
        }
      }
      shippingMethodId
      shippingMethod {
        shippingMethodId
        shippingMethodName
        carrierCode
        carrierId
        serviceCode
        packageType
        maxWeight
      }
    }
  }
`;

// Get user email by ID
export const GET_USER_EMAIL = gql`
  query GetUserEmail($userId: Int!) {
    userById(id: $userId) {
      userId
      username
      fullName
      email
    }
  }
`;

// Get targeted shipping rate (fast, single rate)
export const GET_TARGETED_SHIPPING_RATE = gql`
  query GetTargetedShippingRate(
    $orderId: Int!,
    $pickTicketId: Long,
    $packageDetails: PackageDetailsInput!,
    $carrierCode: String!,
    $serviceCode: String!,
    $emails: String,
    $labelMessages: LabelMessagesInput,
    $shipToOverride: ShipToOverrideInput
  ) {
    targetedShippingRate(
      orderId: $orderId,
      pickTicketId: $pickTicketId,
      packageDetails: $packageDetails,
      carrierCode: $carrierCode,
      serviceCode: $serviceCode,
      emails: $emails,
      labelMessages: $labelMessages,
      shipToOverride: $shipToOverride
    ) {
      shipmentId
      rate {
        rateId
        serviceCode
        serviceName
        carrierCode
        carrierFriendlyName
        shippingAmount {
          amount
          currency
        }
        insuranceAmount {
          amount
          currency
        }
        confirmationAmount {
          amount
          currency
        }
        deliveryDays
        estimatedDeliveryDate
        guaranteedService
        shipDate
        carrierDeliveryDays
        zone
        packageType
        includesTracking
        includesInsurance
      }
      fallbackUsed
      searchCriteria {
        requestedCarrier
        requestedService
        actualCarrier
        actualService
      }
      errors {
        carrierCode
        message
      }
    }
  }
`;

// Get targeted shipping rate (fast, single rate with multi-package support)
export const GET_TARGETED_SHIPPING_RATE_MULTI_PACKAGE = gql`
  query GetTargetedShippingRateMultiPackage(
    $orderId: Int!,
    $pickTicketId: Long,
    $multiPackageDetails: MultiPackageDetailsInput!,
    $carrierCode: String!,
    $serviceCode: String!,
    $emails: String,
    $labelMessages: LabelMessagesInput,
    $shipToOverride: ShipToOverrideInput
  ) {
    targetedShippingRate(
      orderId: $orderId,
      pickTicketId: $pickTicketId,
      multiPackageDetails: $multiPackageDetails,
      carrierCode: $carrierCode,
      serviceCode: $serviceCode,
      emails: $emails,
      labelMessages: $labelMessages,
      shipToOverride: $shipToOverride
    ) {
      shipmentId
      rate {
        rateId
        serviceCode
        serviceName
        carrierCode
        carrierFriendlyName
        shippingAmount {
          amount
          currency
        }
        insuranceAmount {
          amount
          currency
        }
        confirmationAmount {
          amount
          currency
        }
        deliveryDays
        estimatedDeliveryDate
        guaranteedService
        shipDate
        carrierDeliveryDays
        zone
        packageType
        includesTracking
        includesInsurance
      }
      fallbackUsed
      searchCriteria {
        requestedCarrier
        requestedService
        actualCarrier
        actualService
      }
      errors {
        carrierCode
        message
      }
    }
  }
`;

// Get shipping preferences
export const GET_SHIPPING_PREFERENCES = gql`
  query GetShippingPreferences($customerId: Int!) {
    shippingPreferences(customerId: $customerId) {
      customerId
      speedWeight
      priceWeight
      reliabilityWeight
      preferredCarriers
      avoidCarriers
      maxAcceptablePrice
      preferFlatRate
      ecoFriendlyPreferred
      preferredDeliveryDays
    }
  }
`;

// Update shipping preferences
export const UPDATE_SHIPPING_PREFERENCES = gql`
  mutation UpdateShippingPreferences($input: UpdateShippingPreferencesInput!) {
    updateShippingPreferences(input: $input) {
      customerId
      speedWeight
      priceWeight
      reliabilityWeight
      preferredCarriers
      avoidCarriers
      maxAcceptablePrice
      preferFlatRate
      ecoFriendlyPreferred
      preferredDeliveryDays
    }
  }
`;

// Get shipping configuration
export const GET_SHIPPING_CONFIGURATION = gql`
  query GetShippingConfiguration {
    shippingConfiguration {
      testMode
      supportsMultipleNotificationEmails
    }
  }
`;